// The "unverified" tag on a job (Spencer, 2026-10-16): for a login that may verify, it is a link that opens the
// catalog editor in a new tab on the recipe the job item priced through, at the first step or line to sign; its hover
// lists what is unverified on the item (the recipe, each operation, each material, and the children's). For anyone
// else it is the plain tag with the same hover. Verify itself stays in the editor, on the row that uses the operation
// or material (2026-10-12); nothing is signed from a job.

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { ResolvedGroup, ResolvedItem, Resolution } from '@zerogap/engine';
import { useSession } from '../lib/session';
import { Tag } from './kit';

/** The catalog editor at a catalog item's recipe and, when given, a row: `undefined` picks the item's first unverified row; `null` means none. */
export function editorAddress(catalogItemId: string, recipeId: string | null, row: string | null): string {
  const q = new URLSearchParams();
  if (recipeId) q.set('recipe', recipeId);
  if (row) q.set('row', row);
  const s = q.toString();
  return `/library/catalog/${catalogItemId}${s ? `?${s}` : ''}`;
}

/** Where a job item's unverified content is signed: its catalog item, on the recipe it priced through, at the row given or the first to sign. */
export function verifyAddress(item: ResolvedItem, row?: string | null): string | null {
  if (!item.catalog_item_id || item.is_custom) return null;
  const r = row === undefined ? item.unverified.steps[0]?.step_id ?? item.unverified.lines[0]?.line_id ?? null : row;
  return editorAddress(item.catalog_item_id, item.recipe_id, r);
}

/** The editor at a group's recipe and row: the Labor and Materials tabs' by-kind-of-item view. A job-route group goes to its job type. */
export function groupAddress(g: ResolvedGroup, row: string | null, jobTypeId: string | null): string | null {
  if (g.kind === 'job') return jobTypeId ? `/library/job_type?row=${jobTypeId}` : null;
  return g.catalog_item_id ? editorAddress(g.catalog_item_id, g.recipe_id, row) : null;
}

/** For an operation line across the job: the first item whose route uses it unverified, at that step; a job-route operation goes to the job type. */
export function operationAddress(res: Resolution, operationId: string, jobTypeId: string | null): string | null {
  for (const i of res.items) {
    const s = i.unverified.steps.find((x) => x.operation_id === operationId);
    if (s) return verifyAddress(i, s.step_id);
  }
  return res.groups.some((g) => g.kind === 'job' && g.steps.some((s) => s.operation_id === operationId)) && jobTypeId ? `/library/job_type?row=${jobTypeId}` : null;
}

/** For a material line across the job: the first item whose BOM buys it unverified, at that line; a job-route material goes to the job type. */
export function materialAddress(res: Resolution, materialId: string, jobTypeId: string | null): string | null {
  for (const i of res.items) {
    const l = i.unverified.lines.find((x) => x.material_id === materialId);
    if (l) return verifyAddress(i, l.line_id);
  }
  return res.groups.some((g) => g.kind === 'job' && g.bom.some((b) => b.material_id === materialId)) && jobTypeId ? `/library/job_type?row=${jobTypeId}` : null;
}

/** What is unverified on an item, one line each, for the tag's hover. `named` puts the item's number first: a child under its parent. */
export function unverifiedWords(item: ResolvedItem, named = false): string[] {
  const pre = named ? `${item.number} · ` : '';
  const out: string[] = [];
  if (item.is_custom) return [`${pre}custom item: priced from what was typed on it`];
  if (item.unverified.recipe) out.push(`${pre}recipe ${item.recipe_name ?? ''} is not signed`.replace('  ', ' '));
  for (const s of item.unverified.steps) out.push(`${pre}operation: ${s.operation_name}`);
  for (const l of item.unverified.lines) out.push(`${pre}material: ${l.description}`);
  return out;
}

/**
 * The tag. `items` is the item and whatever is folded into its line (a cabinet with its doors and drawer boxes on the
 * Price tab); the link goes to the first of them with something to sign, and the hover lists all of them. `to` and
 * `title` replace both for a tag on one row (a BOM line, a route step, an operation across the job).
 */
export function UnverifiedTag({ items, to, title, children = 'unverified' }: { items: ResolvedItem[]; to?: string | null; title?: string; children?: ReactNode }) {
  const { can } = useSession();
  const first = items.find((i) => i.unverified.any && !i.is_custom) ?? null;
  const words = title ?? items.flatMap((i) => unverifiedWords(i, items.length > 1 || !!i.parent_item_id)).join('\n');
  const address = to === undefined ? (first ? verifyAddress(first) : null) : to;
  // no words (the item tree, F5 2026-10-24): the hatch alone is the mark, the key at the top names it, the hover says what
  const mark = children === '';
  if (!can('verify') || !address) return mark ? <span className="tag un mark" title={words} aria-label="unverified" /> : <Tag kind="un" title={words}>{children}</Tag>;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Link className={"tag un" + (mark ? ' mark' : '')} aria-label={mark ? 'unverified' : undefined} to={address} target="_blank" rel="noopener" title={`${words}\n\nOpens the catalog editor in a new tab, at the row to sign.`} onClick={stop} onMouseDown={stop} onKeyDown={stop}>
      {children}
    </Link>
  );
}

/**
 * The tag a job row carries while an AI employee's edit on it waits to be verified (docs/ai-edit-rights-brief.md,
 * 2026-10-22): who changed it and since when; for a login that may verify, a link to the before/after view, where one
 * Verify covers every edit on the row, or Send back returns it to the version last verified.
 */
export function AiChangedTag({ table, row, users, short }: { table: 'job_item' | 'scope_row' | 'job'; row: { id: string; unverified_since?: string | null; unverified_by?: string | null }; users: { id: string; name: string }[]; short?: boolean }) {
  const { can, user } = useSession();
  if (!row.unverified_since) return null;
  const by = users.find((u) => u.id === row.unverified_by)?.name ?? 'an AI employee';
  const when = String(row.unverified_since).slice(0, 10);
  const words = `Changed by ${by} on ${when}; not yet verified. The job cannot pass Engineering until it is.`;
  // one mark and one name for the state, on every screen (Iris, 2026-10-22): the hatch Spencer chose for Unverified, and who
  const label = `Unverified · ${by}`;
  void short;
  if (!can('verify') || user?.is_ai) return <span className="tag un hatched" title={words}>{label}</span>;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  return (
    <Link className="tag un hatched" to={`/verify/${table}/${row.id}`} title={`${words}\n\nOpens the before/after view: Verify, or Send back with a note.`} onClick={stop} onMouseDown={stop} onKeyDown={stop}>
      {label}
    </Link>
  );
}
